import { styled } from '@mui/material';
import { memo, useMemo } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../Common/_hooks/useAppSelector';
import SizeDisplay from '../../Common/Measurements/SizeDisplay';
import SizeRangeDisplay from '../../Common/Measurements/SizeRangeDisplay';
import type { Locale } from '../../UserData/_stores/UserPrefsStore';
import bodyMeasureIllustration0EnUrl from '@resources/img/pages/products/_sections/sizes/body-measure-illustration-0-en_CA.svg?url';
import bodyMeasureIllustration0FrUrl from '@resources/img/pages/products/_sections/sizes/body-measure-illustration-0-fr_CA.svg?url';
import bodyMeasureIllustration1EnUrl from '@resources/img/pages/products/_sections/sizes/body-measure-illustration-1-en_CA.svg?url';
import bodyMeasureIllustration1FrUrl from '@resources/img/pages/products/_sections/sizes/body-measure-illustration-1-fr_CA.svg?url';
import bodyMeasureIllustration2EnUrl from '@resources/img/pages/products/_sections/sizes/body-measure-illustration-2-en_CA.svg?url';
import bodyMeasureIllustration2FrUrl from '@resources/img/pages/products/_sections/sizes/body-measure-illustration-2-fr_CA.svg?url';
import bodyMeasureIllustration3EnUrl from '@resources/img/pages/products/_sections/sizes/body-measure-illustration-3-en_CA.svg?url';
import bodyMeasureIllustration3FrUrl from '@resources/img/pages/products/_sections/sizes/body-measure-illustration-3-fr_CA.svg?url';
import bodyMeasureIllustration4EnUrl from '@resources/img/pages/products/_sections/sizes/body-measure-illustration-4-en_CA.svg?url';
import bodyMeasureIllustration4FrUrl from '@resources/img/pages/products/_sections/sizes/body-measure-illustration-4-fr_CA.svg?url';
import bodyMeasureIllustration5EnUrl from '@resources/img/pages/products/_sections/sizes/body-measure-illustration-5-en_CA.svg?url';
import bodyMeasureIllustration5FrUrl from '@resources/img/pages/products/_sections/sizes/body-measure-illustration-5-fr_CA.svg?url';

const SizeChart = styled('table')(({theme}) => ({
    fontFamily: 'Poppins, sans-serif',
	textTransform: 'uppercase',
	fontWeight: '500',
    fontSize: '1.25rem',
    borderCollapse: 'collapse',
    
	'tr': {
		'th': {
			color: '#F600BE',
            fontWeight: '600',
		},
		'th,td': {
			padding: '10px',
            textAlign: 'center',
			border: '1px solid rgba(255,255,255,0.8)',
            
            [theme.breakpoints.down('sm')]: {
                padding: '5px 2px',
            },
		},
		'&:first-of-type th': {
			borderTop: '0',
            '&:first-of-type': {
                maxWidth: '120px',
            },
		},
		'&:last-child td': {
			borderBottom: '0',
		},
		'td:first-of-type,th:first-of-type': {
			borderLeft: '0',
		},
		'td:last-child, th:last-child': {
			borderRight: '0',
		},
		'&.chart-head': {
			'th': {
			borderBottom: '1px solid white',
			},
		},
	},
}))

const SizeChartWrapper = styled('div')(({theme}) => ({
    margin: '40px auto 0',
   
    [theme.breakpoints.up('md')]: {
        display: 'flex',
        alignItems: 'center',
        maxWidth: '1000px',
    },

    'img': {
        marginTop: '0',
        
        [theme.breakpoints.up('md')]: {
            marginRight: '60px',
        },
        
        [theme.breakpoints.down('md')]: {
            marginBottom: '30px',
            marginLeft: '50px',
        },
        
        [theme.breakpoints.down('sm')]: {
            maxWidth: '210px',
        },
    }
}))

type SectionSizeChart = 'tops' | 'bottoms' | 'full' | 'bottoms-men' | 'polos-men' | 'polos-women' | 'unisex-joggers'
type ClothingSize = 'xsmall'|'small'|'medium'|'large'|'xlarge'|'xxlarge'|'2xlarge'|'3xlarge'

interface SizeChartColumn {
    label: string
    slug: ClothingSize
}

interface MeasurementValue {
    inches: number
}

interface MeasurementRange {
    maxInches: number
    minInches: number
}

type SizeChartCell = MeasurementValue | MeasurementRange | null;

interface SizeChartRow {
    charts?: SectionSizeChart[]
    label: string
    values: Partial<Record<ClothingSize, SizeChartCell>>
}

interface SizeChartConfig {
    columns: SizeChartColumn[]
    rows: SizeChartRow[]
    secondaryHeaderValues?: Partial<Record<ClothingSize, string>>
}

const standardColumns: SizeChartColumn[] = [
    { slug: 'xsmall', label: 'XS' },
    { slug: 'small', label: 'S' },
    { slug: 'medium', label: 'M' },
    { slug: 'large', label: 'L' },
    { slug: 'xlarge', label: 'XL' },
    { slug: 'xxlarge', label: 'XXL' },
];

const poloColumns: SizeChartColumn[] = [
    { slug: 'small', label: 'SM' },
    { slug: 'medium', label: 'MD' },
    { slug: 'large', label: 'L' },
    { slug: 'xlarge', label: 'XL' },
    { slug: '2xlarge', label: '2XL' },
    { slug: '3xlarge', label: '3XL' },
];

const joggerColumns: SizeChartColumn[] = poloColumns.slice(0, 5);

const standardSecondaryHeaderValues: Partial<Record<ClothingSize, string>> = {
    xsmall: '0-2',
    small: '4-6',
    medium: '8-10',
    large: '12-14',
    xlarge: '16',
    xxlarge: '20-22',
};

const chartTitleKeys: Record<SectionSizeChart, string> = {
    tops: 'Size',
    bottoms: 'Women\'s US Size',
    full: 'Women\'s US Size',
    'bottoms-men': 'Size',
    'polos-men': 'Size',
    'polos-women': 'Size',
    'unisex-joggers': 'Size',
};

const chartImageUrls: Record<Locale, Record<SectionSizeChart, string>> = {
    en_CA: {
        tops: bodyMeasureIllustration0EnUrl,
        bottoms: bodyMeasureIllustration1EnUrl,
        full: bodyMeasureIllustration2EnUrl,
        'bottoms-men': bodyMeasureIllustration3EnUrl,
        'polos-men': bodyMeasureIllustration4EnUrl,
        'polos-women': bodyMeasureIllustration4EnUrl,
        'unisex-joggers': bodyMeasureIllustration5EnUrl,
    },
    fr_CA: {
        tops: bodyMeasureIllustration0FrUrl,
        bottoms: bodyMeasureIllustration1FrUrl,
        full: bodyMeasureIllustration2FrUrl,
        'bottoms-men': bodyMeasureIllustration3FrUrl,
        'polos-men': bodyMeasureIllustration4FrUrl,
        'polos-women': bodyMeasureIllustration4FrUrl,
        'unisex-joggers': bodyMeasureIllustration5FrUrl,
    },
};

const inches = (value: number): MeasurementValue => ({
    inches: value,
});

const inchRange = (minInches: number, maxInches: number): MeasurementRange => ({
    maxInches,
    minInches,
});

const standardRows: SizeChartRow[] = [
    {
        charts: ['full', 'tops'],
        label: 'Bust',
        values: {
            xsmall: inchRange(32, 34),
            small: inchRange(34, 37),
            medium: inchRange(37, 40),
            large: inchRange(40, 41),
            xlarge: inchRange(41, 42),
            xxlarge: inchRange(42, 43),
        },
    },
    {
        label: 'Waist',
        values: {
            xsmall: inchRange(24, 26),
            small: inchRange(26, 29),
            medium: inchRange(29, 32),
            large: inchRange(33, 37),
            xlarge: inchRange(37, 42),
            xxlarge: inchRange(42, 44),
        },
    },
    {
        charts: ['full', 'bottoms'],
        label: 'Hips',
        values: {
            xsmall: inchRange(33, 35),
            small: inchRange(35, 37),
            medium: inchRange(38, 40),
            large: inchRange(41, 43),
            xlarge: inchRange(43, 46),
            xxlarge: inchRange(46, 50),
        },
    },
];

const sizeChartConfigs: Record<SectionSizeChart, SizeChartConfig> = {
    full: {
        columns: standardColumns,
        rows: standardRows,
        secondaryHeaderValues: standardSecondaryHeaderValues,
    },
    bottoms: {
        columns: standardColumns,
        rows: standardRows,
        secondaryHeaderValues: standardSecondaryHeaderValues,
    },
    tops: {
        columns: standardColumns,
        rows: standardRows,
    },
    'bottoms-men': {
        columns: standardColumns,
        rows: standardRows,
    },
    'polos-men': {
        columns: poloColumns,
        rows: [
            {
                label: 'Body Length',
                values: {
                    small: inches(28),
                    medium: inches(29),
                    large: inches(30),
                    xlarge: inches(31),
                    '2xlarge': inches(32),
                    '3xlarge': inches(33),
                },
            },
            {
                label: 'Chest Width',
                values: {
                    small: inches(19),
                    medium: inches(21),
                    large: inches(23),
                    xlarge: inches(25),
                    '2xlarge': inches(27),
                    '3xlarge': inches(29),
                },
            },
        ],
    },
    'polos-women': {
        columns: poloColumns.slice(0, 5),
        rows: [
            {
                label: 'Body Length',
                values: {
                    small: inches(24.75),
                    medium: inches(25.5),
                    large: inches(26.25),
                    xlarge: inches(27),
                    '2xlarge': null,
                },
            },
            {
                label: 'Chest Width',
                values: {
                    small: inches(18),
                    medium: inches(19),
                    large: inches(20),
                    xlarge: inches(21),
                    '2xlarge': null,
                },
            },
        ],
    },
    'unisex-joggers': {
        columns: joggerColumns,
        rows: [
            {
                label: 'Width',
                values: {
                    small: inches(13.5),
                    medium: inches(14.5),
                    large: inches(15.5),
                    xlarge: inches(18),
                    '2xlarge': inches(18),
                },
            },
            {
                label: 'Length',
                values: {
                    small: inches(39),
                    medium: inches(40),
                    large: inches(40.5),
                    xlarge: inches(41),
                    '2xlarge': inches(41.5),
                },
            },
            {
                label: 'Hip',
                values: {
                    small: inches(20),
                    medium: inches(21),
                    large: inches(22),
                    xlarge: inches(23),
                    '2xlarge': inches(24),
                },
            },
            {
                label: 'Knee Width',
                values: {
                    small: inches(8.3),
                    medium: inches(8.7),
                    large: inches(9.1),
                    xlarge: inches(9.4),
                    '2xlarge': inches(9.8),
                },
            },
        ],
    },
};

const isMeasurementRange = (value: SizeChartCell): value is MeasurementRange => {
    return value !== null && 'minInches' in value;
}

const formatSizeChartCell = (value: SizeChartCell | undefined): React.ReactNode => {
    if (value === undefined || value === null) {
        return null;
    }

    if (isMeasurementRange(value)) {
        return <SizeRangeDisplay
            minSizes={[value.minInches]}
            maxSizes={[value.maxInches]}
            sourceUnit="in"
            displayType="physical"
        />;
    }

    return <SizeDisplay sizes={[value.inches]} sourceUnit="in" displayType="physical" />;
}

type Props = React.PropsWithChildren<{
    title: React.ReactNode
    text: React.ReactNode
    chart?: SectionSizeChart
    productId: number
}>

export default memo(function SectionSizes(props: Props) {
    const [t] = useTranslation('page/products/_sections');

    const productData = useAppSelector(state => state.get('appData').get('products').get(String(props.productId)));
    const locale = useAppSelector(state => state.get('userData').get('prefs').get('lang'))

    const sizes = useMemo(() => {
        return productData?.get('availableOptionTypes').get('1')?.get('options').valueSeq().map(option => option.get('slug')).toArray() ?? []
    }, [productData])

    const [chartTitle, chartImg] = useMemo(():[string|undefined,string|undefined] => {
        if (props.chart === undefined) {
            return [t('Size'), undefined];
        }

        return [
            t(chartTitleKeys[props.chart]),
            chartImageUrls[locale][props.chart],
        ];
    }, [props.chart, locale, t])

    const chartConfig = props.chart !== undefined ? sizeChartConfigs[props.chart] : undefined;
    const visibleColumns = useMemo(() => {
        return chartConfig?.columns.filter(column => sizes.includes(column.slug)) ?? [];
    }, [chartConfig, sizes])
    const visibleRows = useMemo(() => {
        if (chartConfig === undefined || props.chart === undefined) {
            return [];
        }

        return chartConfig.rows.filter(row => row.charts === undefined || row.charts.includes(props.chart));
    }, [chartConfig, props.chart])

    return <div className="full-page-panel white-text section-sizes">
        <div className="container">
            <div className="row no-margin">
                <div className="col s12 l12 center-align">
                    <h1>{props.title}</h1>
                    <p>{props.text}</p>

                    { chartImg && props.chart && chartConfig ? <div className="measurement">
                        <SizeChartWrapper>
                            <img className="size-chart-image" src={chartImg} alt="measurement-image"/>
                            <div>
                                <SizeChart className="size-chart">
                                    <thead>
                                        <tr>
                                            <th rowSpan={chartConfig.secondaryHeaderValues !== undefined ? 2 : undefined}>{chartTitle}</th>
                                            {visibleColumns.map(column => <th key={column.slug}>{column.label}</th>)}
                                        </tr>
                                        {chartConfig.secondaryHeaderValues !== undefined ? <tr className="chart-head">
                                            {visibleColumns.map(column => <th key={column.slug}>{chartConfig.secondaryHeaderValues?.[column.slug]}</th>)}
                                        </tr> : null}
                                    </thead>
                                    <tbody>
                                        {visibleRows.map(row => <tr key={row.label}>
                                            <td>{t(row.label)}</td>
                                            {visibleColumns.map(column => <td key={column.slug}>{formatSizeChartCell(row.values[column.slug])}</td>)}
                                        </tr>)}
                                    </tbody>
                                </SizeChart>
                            </div>
                        </SizeChartWrapper>
                    </div> : null }

                    {/*<div className="download-button">
                        <a href="<?=$downloadButton?>" className="btn-2020-flat">Download measurement charts</a>
                    </div> */ }

                    { props.children }
                </div>
            </div>
        </div>
    </div>
})
